<script lang="ts">
export default {
  name: 'ToolSelector'
}
</script>

<script setup lang="ts">
import { StoreMutationType } from '@/types/StoreMutationType';
import { AppState, Commit } from '@/store';
import { SelectionType } from '@/types/tower/Tower';
import { computed, inject } from 'vue';
import { Store } from 'vuex';

const store = inject<Store<AppState>>('store');

const selected = computed(() => {
  return store?.state.towerCanvasState.selection;
});

function select(selection: SelectionType) {

  // if mode is already 'selection', then toggle it to 'pan' mode
  if (selected.value === 'selection' && selection === 'selection') {
    Commit(StoreMutationType.SET_TOWER_CANVAS_SELECTION_TYPE, 'pan');
    return;
  }

  Commit(StoreMutationType.SET_TOWER_CANVAS_SELECTION_TYPE, selection);
}
</script>

<template>
  <section class="h-fit toolbar col-start-2 justify-self-center items-start">
    <div class="toolbar-items shadow-lg">
      <label title="Selection" :class="selected === 'selection' && 'selected'">
        <input type="radio" class="absolute invisible" value="selection" @click="select('selection')" />
        <span class="icon">
          <svg viewBox="0 0 320 512">
            <path
              d="M302.189 329.126H196.105l55.831 135.993c3.889 9.428-.555 19.999-9.444 23.999l-49.165 21.427c-9.165 4-19.443-.571-23.332-9.714l-53.053-129.136-86.664 89.138C18.729 472.71 0 463.554 0 447.977V18.299C0 1.899 19.921-6.096 30.277 5.443l284.412 292.542c11.472 11.179 3.007 31.141-12.5 31.141z"
            ></path>
          </svg>
        </span>
      </label>

      <label title="Rectangle" :class="selected === 'labeled-rect' && 'selected'">
        <input type="radio" class="absolute invisible" value="rectangle" @click="select('labeled-rect')"/>
        <span class="icon">
          <svg viewBox="0 0 448 512">
            <path
              d="M400 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48z"
            ></path>
          </svg>
        </span>
      </label>

      <label title="Triangle" :class="selected === 'triangle' && 'selected'">
        <input type="radio" class="absolute invisible" value="triangle" @click="select('triangle')"/>
        <span class="icon">
          <svg viewBox="0 0 531.74 460.5">
            <path d="m0.866 460 265-459 265 459z" stroke="#000" />
          </svg>
        </span>
      </label>

      <label title="Text" :class="selected === 'text' && 'selected'">
        <input type="radio" class="absolute invisible" value="text" @click="select('text')"/>
        <span class="icon">
          <svg viewBox="0 0 448 512">
            <path
              d="M432 416h-23.41L277.88 53.69A32 32 0 0 0 247.58 32h-47.16a32 32 0 0 0-30.3 21.69L39.41 416H16a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h128a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16h-19.58l23.3-64h152.56l23.3 64H304a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h128a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zM176.85 272L224 142.51 271.15 272z"
            ></path>
          </svg>
        </span>
      </label>

      <label title="Tower Scale" :class="selected === 'tower-scale' && 'selected'">
        <input type="radio" class="absolute invisible" value="tower-scale" @click="select('tower-scale')"/>
        <span class="flex items-center justify-center h-8 w-9">
          <svg viewBox="0 0 24 24" class="h-5 w-5">
            <path d="M10,13H22V11H10M10,19H22V17H10M10,7H22V5H10M6,7H8.5L5,3.5L1.5,7H4V17H1.5L5,20.5L8.5,17H6V7Z" />
          </svg>
        </span>
      </label>
    </div>
  </section>
</template>

<style scoped>
@reference "@/assets/styles/global.css";
.toolbar {
  display: flex;
  flex-direction: row;
  pointer-events: all;
  padding: 0.5rem 0 0.5rem 0.5rem;
  align-items: center;
}

.toolbar-items {
  display: flex;
  box-sizing: border-box;
  box-shadow: 1px 1px 3px rgb(0, 0, 0.3);
  border-radius: 0.5rem;
  opacity: 1;
  background-color: var(--tower-v2-alt-bg-color);
  border: 1px solid var(--tower-v2-border-color);
}

label {
  display: flex;
  align-items: center;
  border-radius: 0.5rem;
  margin: 3px;
  cursor: pointer;
}

label:hover {
  @apply bg-gray-600/10 ease-in;
}

.icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
}

label.selected {
  background-color: var(--tower-v2-tool-selected-color);
}

.icon svg {
  height: 1em;
}
</style>
